import { Types } from "../actionTypes";
import { combineReducers } from "@reduxjs/toolkit";
import { getCookie } from "../../utils/cookie.utils";
import {
  VWO_COOKIE,
  VWO_COOKIE_VALUE_OPT_1,
  ONLINE_OPTIONS,
  SORT_OPTIONS,
  CURRENT_NAV_COOKIE,
} from "../../constants";

const vwoCookie = getCookie(VWO_COOKIE);
const currentNavCookie = getCookie(CURRENT_NAV_COOKIE);
const currentNav = currentNavCookie ? JSON.parse(currentNavCookie) : null;

export const initialState = {
  selectedCategoryTemp: {},
  selectedCategory: {
    id: null,
    hasChildren: false,
    name: null,
    parentCategory: null,
  },
  selectedSubCategory: {},
  futureSubCategory: null,
  // Reason `futureSubCategory` is needed is because currently the API does not return the needed parent category data on a subcategory search
  // So to get around that, there needs to be an additional search for the sub category after the root category is fetched
  // This is only needed/used on a sub category search that has a blank or different root category.

  filterCategoryId: 0,
  // resultsPerPage: 180, // Kevin said we're going to default to 20, and for now remove the ability to change # of results per page.
  filterCurrentPage: 1,
  online:
    vwoCookie === VWO_COOKIE_VALUE_OPT_1 && currentNav
      ? currentNav.offer_filter
      : ONLINE_OPTIONS.ONLY, //options:::: include, only, none
  distance: "50mi",
  locationTerm: "",
  currentLocation: {
    name: "",
    lat: "",
    lon: "",
  },
  useCurrentLocationMsgOpen: false,
  useCurrentLocationMsgDisabled: false,
  sortBy: vwoCookie === "2" && currentNav ? currentNav.sort_by : SORT_OPTIONS.DISTANCE,
  redemptionMethods: [],
  phrase: null, // make null if keyword is set by Director component
  tempPhrase: null,
  selectedCategoryIds: [],
  offerInfo: {},
  offerData: [],
  offerLinks: {},
  isFetching: false,
  errorFetching: false,
  keyword: null, // make null if phrase is set by user
  customSubCategories: {},
};

const resetCurrentPage = (state) => {
  return {
    ...state,
    filterCurrentPage: initialState.filterCurrentPage,
  };
};

const getCategoriesReset = () => {
  return {
    selectedCategory: initialState.selectedCategory,
    selectedCategoryIds: initialState.selectedCategoryIds,
    selectedSubCategory: initialState.selectedSubCategory,
  };
};

const getFiltersReset = (state) => {
  // need to reset all search filters to initial state - except currentLocation, locationTerm & sortBy.
  let newState = initialState;
  newState.currentLocation = state.currentLocation;
  newState.locationTerm = state.locationTerm;
  newState.sortBy = state.sortBy;
  newState.customSubCategories = state.customSubCategories;

  if (state.online !== "") {
    newState.online = state.online;
  }

  if (state.redemptionMethods !== "") {
    newState.redemptionMethods = state.redemptionMethods;
  }

  return newState;
};

const handleSetLocation = (location, state) => {
  // along with setting a location, we also need to set a phrase if the user has entered/changed on
  // but didn't submit prior to setting/changing a location.  Also, if setting a phrase, reset
  // all the filters, and reset to page 1 either way.
  let filtersReset = state;
  let { phrase, tempPhrase } = state;
  if (tempPhrase !== null) {
    phrase = tempPhrase;
    filtersReset = getFiltersReset(state);
  } else if (filtersReset.online === ONLINE_OPTIONS.ONLY && vwoCookie !== VWO_COOKIE_VALUE_OPT_1) {
    filtersReset.online = ONLINE_OPTIONS.INCLUDE;
  }

  return {
    ...filtersReset,
    phrase,
    currentLocation: location,
    filterCurrentPage: initialState.filterCurrentPage,
    useCurrentLocationMsgDisabled: true,
  };
};

const offerFilter = (state = initialState, action) => {
  const { type } = action;
  switch (type) {
    case Types.VIEW_MORE_RESET: {
      const categoriesReset = getCategoriesReset();
      return resetCurrentPage({
        ...state,
        ...categoriesReset,
        customSubCategories: state.customSubCategories,
        phrase: initialState.phrase,
        keyword: initialState.keyword,
        online: initialState.online,
        sortBy: initialState.sortBy,
        distance: initialState.distance,
      });
    }
    case Types.SELECT_KEYWORD: {
      const { keyword } = action;
      const filtersReset = getFiltersReset(state);
      return {
        ...filtersReset,
        phrase: null,
        keyword,
      };
    }
    case Types.SELECT_PHRASE: {
      const { phrase } = action;
      const filtersReset = getFiltersReset(state);
      return {
        ...filtersReset,
        phrase: phrase,
        keyword: null,
      };
    }

    case Types.SELECT_CATEGORY_PHRASE: {
      const { phrase } = action;
      return {
        ...state,
        phrase: phrase,
        keyword: null,
      };
    }

    case Types.SET_TEMP_PHRASE: {
      const { phrase } = action;
      return { ...state, tempPhrase: phrase };
    }
    case Types.HANDLE_LOCATION_TERM_CHANGE: {
      const { value } = action;
      return {
        ...state,
        locationTerm: value,
      };
    }
    case Types.SELECT_LOCATION:
    case Types.FETCH_IP_SUCCESS: {
      const { location } = action;

      return handleSetLocation(location, state);
    }

    case Types.SET_CURRENT_LOCATION: {
      const { locationObj } = action;
      return handleSetLocation(locationObj, state);
    }
    case Types.CLOSE_USE_LOCATION_MSG: {
      return {
        ...state,
        useCurrentLocationMsgOpen: false,
      };
    }
    case Types.GET_PREFERENCES_SUCCESS:
    case Types.SET_PREFERENCES_SUCCESS: {
      const { response } = action;
      if (response && response.useCurrentLocationMsgDisabled) {
        return {
          ...state,
          useCurrentLocationMsgDisabled: response.useCurrentLocationMsgDisabled,
        };
      }
      return state;
    }
    case Types.SELECT_CATEGORY_TEMP: {
      const { id } = action;
      return { ...state, selectedCategoryTemp: id };
    }
    case Types.SELECT_CATEGORY: {
      const { id } = action;
      const filtersReset = getFiltersReset(state);
      return {
        ...filtersReset,
        distance: vwoCookie === VWO_COOKIE_VALUE_OPT_1 ? state.distance : initialState.distance,
        selectedCategory: id,
      };
    }
    case Types.SET_CUSTOM_SUB_CATEGORIES: {
      const { subCategories } = action;
      const filtersReset = getFiltersReset(state);
      return {
        ...filtersReset,
        customSubCategories: subCategories,
      };
    }
    case Types.SELECT_SUB_CATEGORY: {
      const { id } = action;
      const filtersReset = getFiltersReset(state);
      return {
        ...filtersReset,
        selectedCategory: state.selectedCategory,
        selectedCategoryIds: state.selectedCategoryIds,
        selectedSubCategory: id,
        distance: vwoCookie === VWO_COOKIE_VALUE_OPT_1 ? state.distance : initialState.distance,
        futureSubCategory: null,
      };
    }
    case Types.SELECT_FUTURE_SUB_CATEGORY: {
      const { id } = action;
      return {
        ...state,
        futureSubCategory: id,
      };
    }
    case Types.RESET_CATEGORY: {
      const categoriesReset = getCategoriesReset();
      return resetCurrentPage({
        ...state,
        ...categoriesReset,
      });
    }
    case Types.RESET_SUB_CATEGORY: {
      return resetCurrentPage({
        ...state,
        selectedSubCategory: initialState.selectedSubCategory,
        customSubCategories: state.customSubCategories,
      });
    }

    case Types.FILTER_CHANGING_RESULTS_PER_PAGE: {
      const { resultsPerPage } = action;
      return { ...state, resultsPerPage: resultsPerPage };
    }
    case Types.FILTER_RESET_CATEGORY: {
      const { id } = action;
      if (id)
        return resetCurrentPage({
          ...state,
          selectedCategoryIds: [id],
        });
      return resetCurrentPage({ ...state, selectedCategoryIds: [] });
    }
    case Types.FILTER_CATEGORY_CHANGE: {
      const { id } = action;
      if (state.selectedCategoryIds.includes(id)) {
        return resetCurrentPage({
          ...state,
          selectedCategoryIds: state.selectedCategoryIds.filter((e) => e !== id),
        });
      }
      return resetCurrentPage({
        ...state,
        selectedCategoryIds: [...state.selectedCategoryIds, id],
      });
    }
    case Types.FILTER_CHANGING_HOW_TO_REDEEM: {
      const { redeemMethod } = action;
      if (state.redemptionMethods.includes(redeemMethod)) {
        return resetCurrentPage({
          ...state,
          redemptionMethods: state.redemptionMethods.filter((e) => e !== redeemMethod),
        });
      }
      return resetCurrentPage({
        ...state,
        redemptionMethods: [...state.redemptionMethods, redeemMethod],
      });
    }
    case Types.FILTER_CHANGING_DISTANCE: {
      const { distance } = action;
      let { useCurrentLocationMsgOpen } = state;
      if (!state.currentLocation.name) {
        useCurrentLocationMsgOpen = true;
      }
      return resetCurrentPage({
        ...state,
        distance: distance,
        useCurrentLocationMsgOpen,
      });
    }
    case Types.FILTER_CHANGING_ONLINE: {
      const { online } = action;
      let { useCurrentLocationMsgOpen } = state;
      if (online === ONLINE_OPTIONS.NONE && !state.currentLocation.name) {
        useCurrentLocationMsgOpen = true;
      }
      return resetCurrentPage({
        ...state,
        online: online,
        useCurrentLocationMsgOpen,
      });
    }
    case Types.FILTER_CHANGING_HOW_TO_SORT: {
      const { sortKey } = action;
      return { ...state, sortBy: sortKey };
    }
    case Types.FILTER_CHANGING_DEALS_NAV: {
      const { sortKey, online } = action.payload;
      let { useCurrentLocationMsgOpen } = state;
      if (online === ONLINE_OPTIONS.NONE && !state.currentLocation.name) {
        useCurrentLocationMsgOpen = true;
      }
      return resetCurrentPage({
        ...state,
        sortBy: sortKey,
        online: online,
        useCurrentLocationMsgOpen,
      });
    }
    case Types.FETCH_SEARCH_RESULTS_SUCCESS: {
      const selectedSubCategory = action.didResetSubCategory ? {} : state.selectedSubCategory;

      return { ...state, selectedSubCategory };
    }
    case Types.CLEAR_OFFER_FILTER_PHRASE: {
      return { ...state, phrase: null };
    }
    default:
      return state;
  }
};

//selectors

export const getOfferFilter = (state) => state.offerFilter;
export const getSelectedCategoryId = (state) => state.offerFilter.selectedCategory;
export const getSelectedSubCategory = (state) => state.offerFilter.selectedSubCategory;
export const getRootCategoryFilterIds = (state) => state.offerFilter.selectedCategoryIds;
export const getZip = (state) => state.offerFilter.currentLocation.name?.split(",").pop().trim();
export const getOfferData = (state) => {
  const offerData = state.offerFilter.offerData.filter((d) => {
    return d.categories.some((c) => state.offerFilter.selectedCategoryIds.includes(c.category_key));
  });
  return {
    ...state.offerFilter,
    offerData,
  };
};

export const getCurrentLocation = (state) => state.offerFilter.currentLocation;
export const getFilterOnline = (state) => state.offerFilter.online;
export const getFilterPhrase = (state) => state.offerFilter.phrase;
export const getCustomSubCategories = (state) => state.offerFilter.customSubCategories;
export const getSearchRadiusDistance = (state) => state.offerFilter.distance;

export default combineReducers({
  offerFilter,
});
